import { formatarIndicador, posicaoTexto } from '../format';
import { useApi } from '../hooks';
import type { IndicadorDoMunicipio, MunicipioIndicadores } from '../types';
import { Carregando } from '../ui';
import { ROTULO_NATUREZA } from './CartaoIndicador';

/** Cor da barra pela posição relativa no estado: vermelho = entre os piores, verde = entre os melhores. */
const corPercentil = (p: number) => (p < 25 ? 'var(--vermelho)' : p < 50 ? 'var(--ambar)' : p < 75 ? 'var(--lima)' : 'var(--verde)');

function Linha({ i }: { i: IndicadorDoMunicipio }) {
  const nat = ROTULO_NATUREZA[i.natureza];
  const valor = formatarIndicador(i.valor, i.unidade, i.casas);
  const medianaTexto = i.mediana !== null ? formatarIndicador(i.mediana, i.unidade, i.casas) : null;
  return (
    <li class="ind-linha" title={`${i.descricao}\nFonte: ${i.fonte.nome} (${i.periodo ?? '—'})`}>
      <div class="ind-topo">
        <span class="ind-nome">{i.nome}</span>
        <strong class="ind-valor">{valor}</strong>
      </div>
      {i.valor === null ? (
        <small class="dica">Sem dado para este município nesta fonte.</small>
      ) : i.percentil !== null ? (
        <>
          <div class="ind-barra" role="img" aria-label={`Melhor que ${Math.round(i.percentil)}% dos municípios`}>
            <div class="ind-barra-fundo" />
            <div class="ind-barra-valor" style={{ width: `${i.percentil}%`, background: corPercentil(i.percentil) }} />
            <div class="ind-barra-mediana" title="Posição mediana do estado" />
          </div>
          <small class="dica">
            {posicaoTexto(i.posicao, i.de)} · estado: mediana {medianaTexto} · {i.periodo}
          </small>
        </>
      ) : (
        <small class="dica">
          {posicaoTexto(i.posicao, i.de)} em valor · estado: mediana {medianaTexto} · {i.periodo} · <span class={`selo selo-tom-${nat.tom}`}>{nat.texto}</span>
        </small>
      )}
    </li>
  );
}

/** Indicadores oficiais do município, por eixo, com a posição entre os 223 e a comparação com o estado. */
export function IndicadoresMunicipio({ cod }: { cod: string }) {
  const c = useApi<MunicipioIndicadores>(`/indicadores/municipio/${cod}`);
  if (c.carregando && !c.dados) return <Carregando texto="Carregando indicadores oficiais…" />;
  if (c.erro || !c.dados) return <p class="dica">Os indicadores oficiais não estão disponíveis agora.</p>;

  return (
    <section class="bloco" aria-label="Indicadores oficiais">
      <h3>Indicadores oficiais</h3>
      <p class="dica">
        Dados do IBGE, Ministério da Saúde e Tesouro Nacional. A barra mostra a posição do município <strong>entre os 223 da Paraíba</strong> (quanto mais cheia, melhor):
        compara com os outros municípios, não com uma meta.
      </p>
      {c.dados.eixos.map((e) => {
        // Indicador que ainda espera planilha (sem nenhum dado no estado) não entra na lista.
        const comDados = e.indicadores.filter((i) => i.periodo !== null);
        return (
        <details key={e.slug} class="secao" open={['educacao', 'saude', 'saneamento'].includes(e.slug)}>
          <summary>
            <span><i class="ponto" style={{ background: e.cor }} />{e.nome}</span>
            {e.indice !== null ? (
              <span class="resumo-sec" title="Média da posição relativa nos indicadores de resultado e estrutura do eixo (0 = pior do estado, 100 = melhor)">
                índice <strong style={{ color: corPercentil(e.indice) }}>{Math.round(e.indice)}</strong>/100
              </span>
            ) : comDados.length === 0 ? (
              <span class="resumo-sec">sem dados</span>
            ) : (
              <span class="resumo-sec">contexto</span>
            )}
          </summary>
          {comDados.length === 0 ? (
            <p class="dica ind-vazio">Ainda não há dados oficiais neste eixo. Eles entram por planilha enviada pelos órgãos responsáveis (por exemplo, a Secretaria de Segurança), na tela Dados oficiais.</p>
          ) : (
            <ul class="ind-lista">
              {comDados.map((i) => <Linha key={i.slug} i={i} />)}
            </ul>
          )}
        </details>
        );
      })}
    </section>
  );
}
